/**
 * Beat sync: re-times a choreography so slide changes land on the music's
 * beats and entrances on its sixteenth notes, keeping the choreography's
 * order and its reading-time guarantees.
 *
 * Kept identical to server/src/services/beatSync.ts — the editor preview and the
 * published render must time a video the same way.
 */
import {
  ChoreographySpec,
  DirectedScene,
  DirectedSpec,
  SlideChoreography,
  VideoChoreography,
  isDirectedSpec,
} from "../models/ChoreographySpec";

/** A constant-tempo beat grid: beat k sits at offset + k * period seconds. */
export interface BeatGrid {
  bpm: number;
  period: number;
  offset: number;
  confidence: number;
  /** The tempo agreed across the track; unstable grids are never used. */
  stable: boolean;
}

/** Entrances finish at least this long before the slide starts leaving. */
const MIN_HOLD_SECONDS = 0.45;
/** A slide may end at most this much earlier than planned to meet a beat. */
const MAX_SHORTEN_SECONDS = 0.25;

const transitionSeconds = (slide: SlideChoreography) =>
  slide.transitionOut.type !== "none" ? slide.transitionOut.durationSeconds : 0;

export function isUsableBeatGrid(grid: BeatGrid | null | undefined): grid is BeatGrid {
  return !!grid && grid.stable && grid.period >= 0.3 && grid.period <= 1.2 && Number.isFinite(grid.offset);
}

/**
 * Apply after speed scaling. Without a usable grid (no music, muted, no clear
 * beat) the choreography comes back unchanged.
 */
export function syncChoreographyToBeats(
  choreography: ChoreographySpec,
  grid: BeatGrid | null | undefined
): ChoreographySpec {
  if (!isUsableBeatGrid(grid) || choreography.slides.length === 0) return choreography;
  const { period, offset } = grid;
  const onGrid = (t: number, step: number, round: (v: number) => number) =>
    offset + round((t - offset) / step - 1e-9) * step;
  const nearestBeat = (t: number) => onGrid(t, period, Math.round);
  const nextBeat = (t: number) => onGrid(t, period, Math.ceil);

  // Arrivals: when each slide is fully on screen (its incoming transition done)
  const slides = choreography.slides;
  const arrivals: number[] = [];
  let sequenceStart = 0;
  slides.forEach((slide, i) => {
    arrivals.push(i === 0 ? 0 : sequenceStart + transitionSeconds(slides[i - 1]));
    sequenceStart += slide.durationSeconds - transitionSeconds(slide);
  });

  const out: SlideChoreography[] = [];
  let arrival = 0;
  slides.forEach((slide, i) => {
    const isLast = i === slides.length - 1;
    const incoming = i === 0 ? 0 : transitionSeconds(slides[i - 1]);
    const outgoing = isLast ? 0 : transitionSeconds(slide);
    const start = arrival - incoming;
    const plannedSpan = isLast
      ? slide.durationSeconds - incoming
      : arrivals[i + 1] - arrivals[i];

    // Entrances keep their offset from the arrival, quantized to sixteenths
    // in their original order; the opening frame of the video stays at 0.
    let latest = -Infinity;
    const overlayEntrances = [...slide.overlayEntrances]
      .map((entrance, index) => ({ entrance, index }))
      .sort((a, b) => a.entrance.delaySeconds - b.entrance.delaySeconds || a.index - b.index)
      .map(({ entrance, index }) => {
        if (entrance.type === "none") return { entrance, index };
        const at = arrival + entrance.delaySeconds - incoming;
        const keepAsIs = (i === 0 && entrance.delaySeconds < 0.1) || at < arrival - 0.05;
        let snapped = keepAsIs ? at : Math.max(Math.min(at, arrival), onGrid(at, period / 4, Math.round));
        snapped = Math.max(snapped, latest);
        latest = snapped;
        return { entrance: { ...entrance, delaySeconds: round3(Math.max(0, snapped - start)) }, index };
      })
      .sort((a, b) => a.index - b.index)
      .map(({ entrance }) => entrance);

    // The slide ends (or hands over) on a beat: the nearest one unless that
    // cuts the reading time short, else the next.
    const target = arrival + plannedSpan;
    let end = nearestBeat(target);
    if (end < target - MAX_SHORTEN_SECONDS || end <= arrival + 0.5) end = nextBeat(target);
    const entrancesDone = Math.max(
      0,
      ...overlayEntrances.filter((e) => e.type !== "none").map((e) => e.delaySeconds + e.durationSeconds)
    );
    while (start + entrancesDone + MIN_HOLD_SECONDS > end - outgoing + 1e-6) end += period;

    out.push({ ...slide, overlayEntrances, durationSeconds: round3(end - start) });
    arrival = end;
  });
  return { ...choreography, slides: out };
}

/** Speed scaling, as every player of a choreography applies it. */
export function applyChoreographySpeed(choreography: ChoreographySpec, speed: number | undefined): ChoreographySpec {
  if (!speed || speed === 1 || speed <= 0) return choreography;
  return {
    ...choreography,
    slides: choreography.slides.map((slide) => ({
      ...slide,
      durationSeconds: slide.durationSeconds / speed,
      overlayEntrances: slide.overlayEntrances.map((e) => ({
        ...e,
        delaySeconds: e.delaySeconds / speed,
        durationSeconds: e.durationSeconds / speed,
      })),
      transitionOut: { ...slide.transitionOut, durationSeconds: slide.transitionOut.durationSeconds / speed },
    })),
  };
}

// ---------------------------------------------------------------------------
// Directed videos
// ---------------------------------------------------------------------------

const sceneTransitionSeconds = (scene: DirectedScene) =>
  scene.transitionOut && scene.transitionOut.type !== "cut" ? scene.transitionOut.durationSeconds : 0;

/** Speed scaling for a directed video: every time in it, scenes to keyframes. */
export function applyDirectedSpeed(spec: DirectedSpec, speed: number | undefined): DirectedSpec {
  if (!speed || speed === 1 || speed <= 0) return spec;
  const at = <T extends { at: number; duration?: number }>(e: T | undefined): T | undefined =>
    e && { ...e, at: e.at / speed, ...(e.duration != null ? { duration: e.duration / speed } : {}) };
  return {
    ...spec,
    scenes: spec.scenes.map((scene) => ({
      ...scene,
      durationSeconds: scene.durationSeconds / speed,
      ...(scene.transitionOut ? { transitionOut: { ...scene.transitionOut, durationSeconds: scene.transitionOut.durationSeconds / speed } } : {}),
      ...(scene.camera ? { camera: scene.camera.map((k) => ({ ...k, t: k.t / speed })) } : {}),
      elements: scene.elements.map((el) => ({
        ...el,
        ...(el.reveal ? { reveal: at(el.reveal) } : {}),
        ...(el.exit ? { exit: at(el.exit) } : {}),
        ...(el.emphasis ? { emphasis: el.emphasis.map((e) => at(e)!) } : {}),
        ...(el.keys ? { keys: el.keys.map((k) => ({ ...k, t: k.t / speed })) } : {}),
      })),
    })),
  };
}

/**
 * Scene changes land on beats; reveals and emphasis on sixteenths. Camera and
 * keyframe motion keep their timing relative to the scene.
 */
export function syncDirectedToBeats(spec: DirectedSpec, grid: BeatGrid | null | undefined): DirectedSpec {
  if (!isUsableBeatGrid(grid) || spec.scenes.length === 0) return spec;
  const { period, offset } = grid;
  const onGrid = (t: number, step: number, round: (v: number) => number) =>
    offset + round((t - offset) / step - 1e-9) * step;
  const scenes = spec.scenes;
  const arrivals: number[] = [];
  let sequenceStart = 0;
  scenes.forEach((scene, i) => {
    arrivals.push(i === 0 ? 0 : sequenceStart + sceneTransitionSeconds(scenes[i - 1]));
    sequenceStart += scene.durationSeconds - sceneTransitionSeconds(scene);
  });

  let arrival = 0;
  const out = scenes.map((scene, i) => {
    const isLast = i === scenes.length - 1;
    const incoming = i === 0 ? 0 : sceneTransitionSeconds(scenes[i - 1]);
    const outgoing = isLast ? 0 : sceneTransitionSeconds(scene);
    const start = arrival - incoming;
    const plannedSpan = isLast ? scene.durationSeconds - incoming : arrivals[i + 1] - arrivals[i];
    const snap = <T extends { at: number }>(e: T): T => {
      const t = start + e.at;
      if ((i === 0 && e.at < 0.1) || t < arrival - 0.05) return e;
      const snapped = Math.max(Math.min(t, arrival), onGrid(t, period / 4, Math.round));
      return { ...e, at: round3(Math.max(0, snapped - start)) };
    };
    const elements = scene.elements.map((el) => ({
      ...el,
      ...(el.reveal ? { reveal: snap(el.reveal) } : {}),
      ...(el.exit ? { exit: snap(el.exit) } : {}),
      ...(el.emphasis ? { emphasis: el.emphasis.map(snap) } : {}),
    }));
    const target = arrival + plannedSpan;
    let end = onGrid(target, period, Math.round);
    if (end < target - MAX_SHORTEN_SECONDS || end <= arrival + 0.5) end = onGrid(target, period, Math.ceil);
    const lastEvent = Math.max(
      0,
      ...elements.flatMap((el) => [
        el.reveal ? el.reveal.at + (el.reveal.duration ?? 0.45) : 0,
        el.exit ? el.exit.at + (el.exit.duration ?? 0.35) : 0,
        ...(el.emphasis ?? []).map((e) => e.at + (e.duration ?? 0.4)),
      ])
    );
    while (start + lastEvent + 0.3 > end - outgoing + 1e-6) end += period;
    arrival = end;
    return { ...scene, elements, durationSeconds: round3(end - start) };
  });
  return { ...spec, scenes: out };
}

/** The choreography as it plays: speed first, then the music's beat. */
export function timeChoreography(choreography: ChoreographySpec, speed: number | undefined, grid: BeatGrid | null | undefined): ChoreographySpec;
export function timeChoreography(choreography: DirectedSpec, speed: number | undefined, grid: BeatGrid | null | undefined): DirectedSpec;
export function timeChoreography(choreography: VideoChoreography, speed: number | undefined, grid: BeatGrid | null | undefined): VideoChoreography;
export function timeChoreography(
  choreography: VideoChoreography,
  speed: number | undefined,
  grid: BeatGrid | null | undefined
): VideoChoreography {
  if (isDirectedSpec(choreography)) return syncDirectedToBeats(applyDirectedSpeed(choreography, speed), grid);
  return syncChoreographyToBeats(applyChoreographySpeed(choreography, speed), grid);
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
